import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Vehicle, User, Charge } from '@clearroadlab/cam';

import { environment } from '../../environments/environment';
import { UsersService } from '../users/users.service';
import { IChargeSearchParams, ChargeSearchResult } from '../charges/charges.service';
import { filterParams, handleAPIError, ISearchParams } from '../utils';
import {RequestSearchResult} from '../requests/requests.service';
import { DeviceSearchResult } from '../devices/devices.service';

const path = `${environment.camApi.baseUrl}/vehicles`;

export interface IVehicleSearchParams extends ISearchParams {
  search?: string;
  searchIn?: string[];
}

export class VehicleExtended extends Vehicle {
  programKey?: string;
  user: Partial<User>;
}

export class VehicleSearchResult {
  count = 0;
  distance = 0;
  results: VehicleExtended[];
}

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  public vehicle: Vehicle;

  constructor(
    private http: HttpClient,
    private usersService: UsersService
  ) { }

  public get({ vin }: Partial<Vehicle>) {
    return this.http.get<Vehicle>(`${path}/${vin}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public getRaw({ vin }: Partial<Vehicle>) {
    return this.http.get<Vehicle>(`${path}/raw/${vin}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public getUser({ vin }: Partial<Vehicle>) {
    return this.http.get<User>(`${path}/${vin}/user`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public list(params?: IVehicleSearchParams) {
    return this.http.get<VehicleSearchResult>(path, {
      headers: this.usersService.headers,
      params: filterParams({
        ...params,
        admin: true
      })
    }).toPromise().catch(handleAPIError);
  }

  public create(vehicle: Partial<Vehicle>, token?: string) {
    return this.http.post<Vehicle>(`${path}/tolling`, vehicle, {
      headers: token ? { 'X-ACCESS-TOKEN': token } : this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }


  public reactivate(vin, token?: string) {
    return this.http.post<Vehicle>(`${path}/reactivate`, vin, {
      headers: token ? { 'X-ACCESS-TOKEN': token } : this.usersService.headers
    });
  }


  async getNetworkConfigs() {
    return this.http.get(
      `${environment.camApi.baseUrl}/network-configs/all`
    ).toPromise().catch(handleAPIError);
  }

  async activateNetworks({vin, networks, startDate, endDate, token}) {
    return this.http.put<Vehicle>(`${path}/${vin}/networks`, { networks, startDate, endDate }, {
      headers: token ? { 'X-ACCESS-TOKEN': token } : this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public update(vehicle: Partial<Vehicle>) {
    return this.http.put<Vehicle>(`${path}/${vehicle.vin}`, vehicle, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public endVehicle(vehicle: Partial<Vehicle>) {
    return this.http.put<Vehicle>(`${path}/${vehicle.vin}/end`, vehicle, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public getActiveVehicle({ license, licenseState }: Partial<Vehicle>): Promise<boolean> {
    return this.http.get<boolean>(`${path}/exists/combination`, {
      headers: this.usersService.headers,
      params: filterParams({
        license,
        licenseState
      })
    }).toPromise().catch(handleAPIError);
  }

  public refreshPricing({ vin }: Partial<Vehicle>) {
    return this.http.put<void>(`${path}/${vin}/pricing`, {}, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public delete({ vin }: Partial<Vehicle>) {
    return this.http.delete(`${path}/${vin}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public createCharge({ charge, sourceId, type, details, status, vehicle, rucDetails }: Partial<Charge>) {
    return this.http.post<Charge>(`${path}/${vehicle.vin}/charges`, { data: {
      vehicle,
      charge,
      sourceId,
      type,
      details,
      status,
      rucDetails
    } }, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public addPoints({ vin }: Partial<Vehicle>, data) {
    return this.http.post<Charge>(`${path}/${vin}/points`, { data }, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public listCharges({ vin }: Partial<Vehicle>, params?: IChargeSearchParams) {
    return this.http.get<ChargeSearchResult>(`${path}/${vin}/charges`, {
      headers: this.usersService.headers,
      params: filterParams(params)
    }).toPromise().catch(handleAPIError);
  }
  public listRequests({ vin }: Partial<Vehicle>, params?: IChargeSearchParams) {
    return this.http.get<RequestSearchResult>(`${path}/${vin}/requests`, {
      headers: this.usersService.headers,
      params: filterParams(params)
    }).toPromise().catch(handleAPIError);
  }

  public listDevices(vin) {
    return this.http.get<DeviceSearchResult>(`${path}/${vin}/devices`, {
      headers: this.usersService.headers,
      params: {
        admin: true
      }
    }).toPromise().catch(handleAPIError);
  }
}
